import { useEffect, useState } from 'react';
import { useAchievementTypes, useReduxState } from 'hooks';
import { useFormApi, useFormState } from 'informed';
import { breakpoints } from 'styles';
import { addMonths } from 'date-fns';
import {
  Checkbox,
  Datepicker,
  MultipleAttachments
} from 'components/common/form';

import type { PageState } from 'types';
import type { AchievementCompletionAddFormValues } from './AchievementCompletionsAdd';

interface Props {
  achievement: PageState['achievement'];
  setUploader: (upload: any) => void;
}

const AchievementCompletionsAddHeader = ({
  achievement,
  setUploader
}: Props) => {
  const formApi = useFormApi();

  const formState = useFormState<AchievementCompletionAddFormValues>();

  const { messages, theme } = useReduxState();

  const { hasCompletion, hasExpiration, hasStartedAt } = useAchievementTypes({
    type: achievement?.type
  });

  const [isMounted, setIsMounted] = useState(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleCompletionDateChange = () => {
    const { completionDate } = formState.values;

    if (achievement.monthsBeforeExpiration)
      formApi.setValue(
        'expirationDate',
        completionDate
          ? addMonths(
              new Date(completionDate),
              achievement.monthsBeforeExpiration
            )
          : null
      );
  };

  const handleCheckboxToggle = (
    value: boolean,
    toggle: 'completion' | 'expiration'
  ) => {
    if (value)
      (formState.values.emails || formState.values.usernames).forEach((id, i) =>
        formApi.setValue(
          `${i}.${toggle}Date`,
          formState.values[`${toggle}Date`] || null
        )
      );
  };

  return (
    <div className="header">
      <div className="options">
        {hasStartedAt ? (
          <div className="option">
            <Checkbox
              field="useCommonStartedAtDate"
              id="useCommonStartedAtDate"
              label={messages.achievementUsersAddCommonStartDate}
            />
            <div className="input">
              <Datepicker
                disabled={!formState.values.useCommonCompletionDate}
                field="startedAtDate"
                id="startedAtDate"
                isClearable={true}
                placeholder={messages.publishStartDateEmpty}
                maxDate={
                  formState.values?.completionDate ||
                  formState.values?.expirationDate
                }
                simpleClear={true}
              />
            </div>
          </div>
        ) : null}

        {hasCompletion ? (
          <div className="option">
            <Checkbox
              field="useCommonCompletionDate"
              id="useCommonCompletionDate"
              label={messages.achievementUsersAddCommonCompletionDate}
              onValueChange={(value: boolean) =>
                handleCheckboxToggle(value, 'completion')
              }
            />
            <div className="input">
              <Datepicker
                disabled={!formState.values.useCommonCompletionDate}
                field="completionDate"
                id="completionDate"
                isClearable={true}
                placeholder={messages.completionsCompletionDateEmpty}
                maxDate={formState.values?.expirationDate}
                minDate={formState.values?.startedAtDate}
                onChange={handleCompletionDateChange}
                simpleClear={true}
              />
            </div>
          </div>
        ) : null}

        {hasExpiration ? (
          <div
            className="option"
            {...(achievement.monthsBeforeExpiration && {
              'aria-label':
                messages.achievementCompletionAutomaticExpirationTooltip,
              'data-tooltip':
                messages.achievementCompletionAutomaticExpirationTooltip
            })}
          >
            <Checkbox
              disabled={Boolean(achievement.monthsBeforeExpiration)}
              field="useCommonExpirationDate"
              id="useCommonExpirationDate"
              label={messages.achievementUsersAddCommonExpirationDate}
              onValueChange={(value: boolean) =>
                handleCheckboxToggle(value, 'expiration')
              }
            />
            <div className="input">
              <Datepicker
                disabled={!formState.values.useCommonExpirationDate}
                field="expirationDate"
                id="expirationDate"
                isClearable={true}
                placeholder={messages.publishEndDateEmpty}
                minDate={
                  formState.values?.completionDate ||
                  formState.values?.startedAtDate
                }
                simpleClear={true}
              />
            </div>
          </div>
        ) : null}

        <div className="option">
          <Checkbox
            field="useCommonAttachments"
            id="useCommonAttachments"
            label={messages.achievementUsersAddCommonAttachment}
          />

          <div className="input" style={{ width: 'max-content' }}>
            {isMounted ? (
              <MultipleAttachments
                disabled={!formState.values.useCommonAttachments}
                getUploadHandler={setUploader}
                useUnwrapped={true}
              />
            ) : null}
          </div>
        </div>
      </div>

      <style jsx>{`
        .header {
          border-bottom: 1px solid ${theme.lightGreyDark};
          border-top: 1px solid ${theme.lightGreyDark};
          padding: 15px 15px 20px;
        }
        .options {
          align-items: flex-start;
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 20px 40px;
          justify-content: flex-start;
          width: 100%;
        }
        .option {
          width: 260px;
        }
        .input {
          margin-top: 8px;
          padding-left: 20px;
          width: 180px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .option {
            width: 220px;
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .option {
            width: 190px;
          }
        }
      `}</style>
    </div>
  );
};

export default AchievementCompletionsAddHeader;
